import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

let authToken = localStorage.getItem("df_token") || null;

export const setAuthToken = (token) => { authToken = token; };

client.interceptors.request.use((config) => {
  if (authToken) config.headers.Authorization = `Bearer ${authToken}`;
  return config;
});

// Download links can't carry a header — signed by the token query fallback is not used,
// so file downloads go through fetch + blob instead (see downloadFile below).
export const downloadFile = async (url, filename) => {
  const res = await fetch(url, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

export const api = {
  authStatus: () => client.get("/auth/status").then((r) => r.data),
  login: (body) => client.post("/auth/login", body).then((r) => r.data),
  register: (body) => client.post("/auth/register", body).then((r) => r.data),
  forgotPassword: (email) => client.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (body) => client.post("/auth/reset-password", body).then((r) => r.data),
  checkInvite: (token) => client.get(`/auth/invite/${token}`).then((r) => r.data),
  getUsage: (days = 30) => client.get("/usage", { params: { days } }).then((r) => r.data),
  getPlans: () => client.get("/plans").then((r) => r.data),
  adminUsers: () => client.get("/admin/users").then((r) => r.data),
  setUserPlan: (id, body) => client.post(`/admin/users/${id}/plan`, body).then((r) => r.data),
  deleteUser: (id) => client.delete(`/admin/users/${id}`).then((r) => r.data),
  listInvites: () => client.get("/invites").then((r) => r.data),
  createInvite: (body) => client.post("/invites", body).then((r) => r.data),
  resendInvite: (id) => client.post(`/invites/${id}/resend`).then((r) => r.data),
  deleteInvite: (id) => client.delete(`/invites/${id}`).then((r) => r.data),
  desktopReleases: () => client.get("/desktop/releases").then((r) => r.data),
  setGithubRepo: (repo) => client.post("/desktop/github", { repo }).then((r) => r.data),
  getEmailSettings: () => client.get("/email/settings").then((r) => r.data),
  listPresets: () => client.get("/presets").then((r) => r.data),
  savePreset: (name, config) => client.post("/presets", { name, config }).then((r) => r.data),
  deletePreset: (id) => client.delete(`/presets/${id}`).then((r) => r.data),
  launchPreset: (id, start = true) =>
    client.post(`/presets/${id}/launch`, null, { params: { start } }).then((r) => r.data),
  sharePreset: (id) => client.post(`/presets/${id}/share`).then((r) => r.data),
  presetGallery: () => client.get("/presets/gallery").then((r) => r.data),
  importGalleryPreset: (slug) => client.post(`/presets/gallery/${slug}/import`).then((r) => r.data),
  setPresetSchedule: (id, body) => client.post(`/presets/${id}/schedule`, body).then((r) => r.data),
  listInvoices: (userId = "") => client.get("/usage/invoices", { params: { user_id: userId } }).then((r) => r.data),
  unsharePreset: (id) => client.delete(`/presets/${id}/share`).then((r) => r.data),
  getSharedPreset: (token) => client.get(`/presets/shared/${token}`).then((r) => r.data),
  importSharedPreset: (token) => client.post(`/presets/shared/${token}/import`).then((r) => r.data),
  saveEmailSettings: (body) => client.post("/email/settings", body).then((r) => r.data),
  refresh: () => client.post("/auth/refresh").then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  changePassword: (body) => client.post("/auth/change-password", body).then((r) => r.data),
  setAuthSettings: (body) => client.post("/auth/settings", body).then((r) => r.data),
  listProviders: () => client.get("/providers").then((r) => r.data),
  saveProvider: (body) => client.post("/providers", body).then((r) => r.data),
  deleteProvider: (p) => client.delete(`/providers/${p}`).then((r) => r.data),
  testProvider: (p, model) => client.post(`/providers/${p}/test`, { model }).then((r) => r.data),
  providerModels: (p) => client.get(`/providers/${p}/models`).then((r) => r.data),
  createCrawl: (cfg) => client.post("/crawls", cfg).then((r) => r.data),
  promptToCrawl: (prompt) => client.post("/crawls/from-prompt", { prompt }).then((r) => r.data),
  listCrawls: () => client.get("/crawls").then((r) => r.data),
  getCrawl: (id) => client.get(`/crawls/${id}`).then((r) => r.data),
  startCrawl: (id) => client.post(`/crawls/${id}/start`).then((r) => r.data),
  stopCrawl: (id) => client.post(`/crawls/${id}/stop`).then((r) => r.data),
  pauseCrawl: (id) => client.post(`/crawls/${id}/pause`).then((r) => r.data),
  resumeCrawl: (id) => client.post(`/crawls/${id}/resume`).then((r) => r.data),
  getCrawlerSettings: () => client.get("/settings/crawler").then((r) => r.data),
  saveCrawlerSettings: (s) => client.post("/settings/crawler", s).then((r) => r.data),
  deleteCrawl: (id) => client.delete(`/crawls/${id}`).then((r) => r.data),
  getPages: (id, skip = 0, limit = 50) =>
    client.get(`/crawls/${id}/pages`, { params: { skip, limit } }).then((r) => r.data),
  getPage: (id, pageId) => client.get(`/crawls/${id}/page/${pageId}`).then((r) => r.data),
  getQuality: (id, refresh = false) =>
    client.get(`/crawls/${id}/quality`, { params: { refresh } }).then((r) => r.data),
  getQualityHistory: (id) => client.get(`/crawls/${id}/quality/history`).then((r) => r.data),
  exportUrl: (id, opts = {}) => {
    const q = new URLSearchParams({
      fmt: opts.fmt || "jsonl",
      dedupe: String(!!opts.dedupe),
      shape: opts.shape || "text_meta",
      chunk_tokens: String(opts.chunk_tokens || 0),
      ...(opts.shape === "training" ? { split: opts.split || "train" } : {}),
      ...(opts.fields?.length ? { fields: opts.fields.join(",") } : {}),
    });
    return `${API}/crawls/${id}/export?${q.toString()}`;
  },
  previewExport: (id, opts) => client.post(`/crawls/${id}/export/preview`, opts).then((r) => r.data),
  saveExport: (id, opts) => client.post(`/crawls/${id}/export/save`, opts).then((r) => r.data),
  listFiles: (crawlId) =>
    client.get("/files", { params: crawlId ? { crawl_id: crawlId } : {} }).then((r) => r.data),
  fileDownloadUrl: (fileId) => `${API}/files/${fileId}/download`,
  deleteFile: (fileId) => client.delete(`/files/${fileId}`).then((r) => r.data),
  getSchedule: (id) => client.get(`/crawls/${id}/schedule`).then((r) => r.data),
  saveSchedule: (id, cfg) => client.post(`/crawls/${id}/schedule`, cfg).then((r) => r.data),
  extendCrawl: (id, mode = "extend") =>
    client.post(`/crawls/${id}/extend`, null, { params: { mode } }).then((r) => r.data),
  listRuns: (id) => client.get(`/crawls/${id}/runs`).then((r) => r.data),
  getRun: (runId) => client.get(`/runs/${runId}`).then((r) => r.data),
  clearRuns: (id) => client.delete(`/crawls/${id}/runs`).then((r) => r.data),
  listVersions: (id) => client.get(`/crawls/${id}/versions`).then((r) => r.data),
  createVersion: (id, deep = false) =>
    client.post(`/crawls/${id}/versions`, null, { params: { deep, reason: "manual" } }).then((r) => r.data),
  compareVersion: (id, vid) => client.get(`/crawls/${id}/versions/${vid}/compare`).then((r) => r.data),
  rollbackVersion: (id, vid) => client.post(`/crawls/${id}/versions/${vid}/rollback`).then((r) => r.data),
  deleteVersion: (id, vid) => client.delete(`/crawls/${id}/versions/${vid}`).then((r) => r.data),
  improvePlan: (id) => client.get(`/crawls/${id}/improve/plan`).then((r) => r.data),
  improveApply: (id, mode) =>
    client.post(`/crawls/${id}/improve/apply`, null, { params: { mode } }).then((r) => r.data),
  discoverSitemap: (body) => client.post("/sitemap/discover", body).then((r) => r.data),
  getDatabaseInfo: () => client.get("/settings/database").then((r) => r.data),
  getActiveLlm: () => client.get("/settings/llm").then((r) => r.data),
  setActiveLlm: (s) => client.post("/settings/llm", s).then((r) => r.data),
  synthesize: (id, body) => client.post(`/crawls/${id}/synthesize`, body).then((r) => r.data),
  startEmpire: (body) => client.post("/empire/start", body).then((r) => r.data),
  empireReport: (id) => client.get(`/crawls/${id}/empire/report`).then((r) => r.data),
  uploadFiles: (fileList, { crawlId, crawlName, runQualityCheck } = {}) => {
    const form = new FormData();
    for (const f of fileList) form.append("files", f);
    if (crawlId) form.append("crawl_id", crawlId);
    if (crawlName) form.append("crawl_name", crawlName);
    form.append("run_quality_check", String(!!runQualityCheck));
    return client.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
};

export const fmtBytes = (b) => {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(1)} ${u[i]}`;
};

export const fmtNum = (n) => (n ?? 0).toLocaleString();
